import { Stack, Flex, Text, IconButton } from '@backyard-ui/core'
import { AirplaneOutline, Plus } from '@backyard-ui/icons'
import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  ChangeEvent,
  KeyboardEvent,
  Dispatch,
  SetStateAction
} from 'react'

import { typebarStyles } from './typebar.styles'

type TypebarProps = {
  useStateMessage: [string, Dispatch<SetStateAction<string>>]
  onSendMessage: (message: string) => void
  onUploadFile: (file: File) => void
  placeholder?: string
  isDisabled?: boolean
  isLoading?: boolean
}

const Typebar = ({
  useStateMessage,
  onSendMessage,
  onUploadFile,
  placeholder,
  isDisabled = false,
  isLoading = false
}: TypebarProps) => {
  const [message, setMessage] = useStateMessage
  const [disabledButton, setDisabledButton] = useState(true)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const styles = typebarStyles()

  const handleSendMessage = useCallback(async () => {
    if (message && !disabledButton && !isLoading) {
      await onSendMessage(message)
      setMessage('')
    }
  }, [message, onSendMessage, disabledButton, isLoading, setMessage])

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        event.preventDefault()
        setMessage(prev => prev + '\n')
      } else {
        event.preventDefault()
        if (message.trim()) {
          handleSendMessage()
        }
      }
    }
  }

  const onChangeMessage = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const text = e.target.value
      setMessage(text)
    },
    [setMessage]
  )

  const handleFileUpload = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target
      if (files && files.length > 0) {
        const file = files[0]
        onUploadFile(file)
        if (!message.length) {
          setMessage('Anexo')
        }
        event.target.value = ''
      }
    },
    [onUploadFile, message.length, setMessage]
  )

  const handleClickIconUpload = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }

    setDisabledButton(!message || message.trim().length < 3)
  }, [message, isLoading])

  const isUploadDisabled = isDisabled || isLoading
  const isSendDisabled = disabledButton

  if (isDisabled) {
    return (
      <Flex
        align="center"
        justify="center"
        className={styles.canNotSendWrapper()}>
        <Text color="gray-600" asChild>
          <p className="text-md md:text-lg">
            Você não pode escrever mensagens nesse momento
          </p>
        </Text>
      </Flex>
    )
  }

  return (
    <Stack align="center" spacing="4" className={styles.wrapper()}>
      <IconButton
        icon={<Plus />}
        size="sm"
        variant="subtle"
        appearance="success"
        radius="full"
        ariaLabel="Upload"
        onClick={handleClickIconUpload}
        isDisabled={isUploadDisabled}
        data-testid="upload-button"
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        onChange={handleFileUpload}
        className={styles.hiddenInput()}
        data-testid="hidden-file-input"
      />

      <textarea
        ref={textareaRef}
        rows={1}
        placeholder={placeholder}
        value={message}
        onChange={onChangeMessage}
        onKeyDown={handleKeyDown}
        className={styles.textArea()}
        data-testid="textarea"
      />

      <Flex align="center" justify="center">
        <IconButton
          icon={isLoading ? <AirplaneOutline /> : <AirplaneOutline />}
          size="sm"
          variant="subtle"
          appearance="success"
          radius="full"
          ariaLabel="Send"
          onClick={handleSendMessage}
          isDisabled={isSendDisabled}
          isLoading={isLoading}
          data-testid="send-button"
        />
      </Flex>
    </Stack>
  )
}

export default Typebar
